import React, { useState } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import set from 'lodash/set';
import { UpdateBillToAndShipToParties } from '@atlassian/cssxp-types-billing';
import {
  useSidebar,
  InfoPanel,
  useFormSubmit,
  AddressFormSectionV2,
  ErrorComponents,
  useNotification,
  NotificationTypes,
  successNotificationBuilder,
  InflightInvoiceBanner,
  getTaxIds,
  getFallbackTaxId,
} from '@css-xp/base';
import { updateShipToParty } from '@atlassian/temp-proxies';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import ChangePricingEligibility from '../component/update-pricing-eligibility';
import './edit-ship-to-party.scss';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';

interface FormData {
  pricingEligibility?: string;
  shipToName?: string;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: { value: string; label: string };
  shipToPostalCode?: string | null;
  shipToState?: { value: string; label: string };
  shipToTaxId?: string | null;
  shipToGst?: string | null;
  shipToPst?: string | null;
}
interface Props {
  transactionAccountId: string;
  pricingEligibility: string | null;
  shipToAddress?: CommerceAddress;
  billToAddress?: CommerceAddress;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  hidePricingEligibility?: boolean;
  // prop to handle the naming convention of the sidebar like sidebar title, notifications sent, etc.
  sidebarType?: string;
  // in case we don't want to fill the "same as billing address" feature
  hideAddFromBillToAddress?: boolean;
  // in case we need to populate few fields by default for adding an address
  // bypassing the logic of opening edit type sidebar everytime ship-to address is present
  isAddType?: boolean;
}

export const EditShipToPartySidebar = ({
  transactionAccountId,
  shipToAddress,
  billToAddress,
  pricingEligibility,
  refetch,
  hidePricingEligibility,
  sidebarType,
  hideAddFromBillToAddress = false,
  isAddType = false,
}: Props) => {
  const { closeSidebar } = useSidebar();

  const isAddShipToAddressFlow = !shipToAddress || isAddType;

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateBillToAndShipToParties, any>({
    submitFormAction: updateShipToParty,
  });

  const [isChecked, setIsChecked] = useState(false);
  //set ship to as bill to if checkbox is selected
  const newshipToAddress = isChecked ? billToAddress : shipToAddress;

  const [countRender, setRenderCount] = useState(0);
  const firstUpdate = React.useRef(true);

  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
  }, [countRender]);

  // const sameAsBillToCheckBoxFeatureFlag = useSelector(
  //   getFeatureFlag(ENABLE_SAME_AS_BILL_TO)
  // );
  const onSameAsBillTo = () => {
    setIsChecked(!isChecked);
    setRenderCount((countRender) => countRender + 1);
  };

  const submitEditBillToPartyDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });
    const taxIds = getTaxIds(billingData);

    const pricingEligibilityValue = {
      [get(transformedData, 'pricingPlan')]: hidePricingEligibility ? 'false' : 'true',
    };

    const updateBillToPartyPayload: UpdateBillToAndShipToParties = {
      pricingEligibility: pricingEligibilityValue,
      transactionAccountId,
      shipToAddress: {
        id: shipToAddress?.id ?? null,
        name: defaultTo(transformedData.shipToName, ''),
        postalAddress: {
          line1: defaultTo(transformedData.shipToAddress1 || null, ''),
          line2: defaultTo(transformedData.shipToAddress2 || null, null),
          city: transformedData?.shipToCity ?? '',
          state: defaultTo(transformedData?.shipToState?.value || null, null),
          postcode: transformedData?.shipToPostalCode ?? '',
          country: transformedData?.shipToCountry?.value ?? '',
        },
        taxId: transformedData?.shipToTaxId || getFallbackTaxId(taxIds),
        taxIds: taxIds.length ? taxIds : undefined,
      },
    };
    submitForm(updateBillToPartyPayload);
  };

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-shipTo-party-success')
      .withHeader(
        `${isAddShipToAddressFlow ? 'Added a new' : 'Successfully updated'} ${
          sidebarType ? sidebarType : 'sold-to address'
        }.`
      )
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    analytics.sendTrackEvent({
      action: isAddShipToAddressFlow ? AnalyticsTrackAction.ADDED : AnalyticsTrackAction.UPDATED,
      actionSubject: AnalyticsTrackSubject.SOLD_TO_ADDRESS,
    });
    setNotificationSent(true);
    closeSidebar();
    if (refetch) {
      refetch();
    }
  }

  const title = (isAddShipToAddressFlow ? 'Add a new ' : 'Edit ') + `${sidebarType ? sidebarType : 'sold-to address'}`;

  return (
    <div className="pane-sidebar-content">
      <Form onSubmit={submitEditBillToPartyDetails}>
        {({ formProps, submitting, setFieldValue }) => (
          <form {...formProps}>
            <FormHeader title={title}></FormHeader>
            <InflightInvoiceBanner
              transactionAccountId={transactionAccountId}
              message={'All changes will be reflected to the orders included in the inflight invoice'}
            />
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to create a new invoice group for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>

            <FormSection>
              {!shipToAddress && !hideAddFromBillToAddress && (
                <Checkbox
                  isChecked={isChecked}
                  onChange={onSameAsBillTo}
                  label="Same as billing address"
                  value="Controlled Checkbox"
                  name="controlled-checkbox"
                />
              )}
              <div className="section">
                <AddressFormSectionV2
                  {...newshipToAddress}
                  isChecked={isChecked}
                  firstUpdate={firstUpdate.current}
                  isEditable={true}
                  addressName="Ship to"
                  setFieldValue={setFieldValue}
                  isRequired={true}
                  hideAddressNameLabel={true}
                  taxIdAddressPrefix={'Sold-to'}
                  isNameReadOnly={!!(isAddType && shipToAddress?.name)}
                />
              </div>
            </FormSection>
            {hidePricingEligibility || (
              <div className="section">
                <ChangePricingEligibility pricingEligibility={pricingEligibility} />
              </div>
            )}
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={false}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  {isAddShipToAddressFlow ? 'Add' : 'Save'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
